import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authFetch } from '@/utils/auth-fetch';
import { RefreshConfirmDialog } from '@/components/category/RefreshConfirmDialog';
import { resolvedSectorForWrite } from '@/components/category/resolveSectorResponse';
import { sectorResolveKey } from '@/components/category/sectorResolveKey';
import { categoryWriteKey, catalogueRootsFrom } from '@/components/category/categoryWriteKey';
import { useSite } from '@/contexts/SiteContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
// The persona-health display is SHARED with pages/admin/CategoryResearch.tsx — see the header of
// PersonaHealth.tsx for why it is a module and not a copy (task-es9vcil1gn).
import {
    PersonaHealthBadges,
    PersonaGrid,
    PersonaEmptyState,
    type PersonaLike,
} from '@/components/category/PersonaHealth';
// The repair action and the guard on the refresh beside it — shared with the admin page for the
// same reason (task-htf7zulfpv).
import {
    PersonaRepairButton,
    PersonaRefreshConfirmDialog,
    weightedCountOf,
    shouldConfirmRefresh,
} from '@/components/category/PersonaRepair';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import {
    Sparkles,
    Loader2,
    Search,
    Plus,
    Edit,
    Trash2,
    ArrowLeft,
    CheckCircle,
    XCircle,
    AlertCircle,
    MessageSquare,
    ThumbsUp,
    ThumbsDown,
    HelpCircle,
    DollarSign,
    RefreshCw,
    Tag,
    Users,
    Ruler,
} from 'lucide-react';

interface VoiceSample {
    id: string;
    phrase: string;
    sentiment: 'positive' | 'neutral' | 'negative' | 'question';
    source: string;
    confidence_score: number;
}

interface PerceivedAttribute {
    id: string;
    attribute_name: string;
    attribute_type: 'functional' | 'use_case' | 'sustainability' | 'maintenance' | 'trust' | 'value';
    customer_description: string;
    common_phrases: string[];
    technical_features: string[];
    voice_samples?: VoiceSample[];
    research_quality_score?: number;
    created_at: string;
    updated_at: string;
}

interface ResearchSession {
    id: string;
    category_name: string;
    status: string;
    parsed_attributes_count: number;
    error_message?: string;
    created_at: string;
    completed_at?: string;
}

interface AttributeIntelligence {
    id: string;
    title: string;
    scope_attribute_name: string;
    scope_category_path: string;
    research_findings: string;
    knowledge_text: string;
    status: string;
    created_at: string;
    updated_at: string;
}

interface PriceTier {
    max: number | null;
    personas: string[];
}

interface CategoryCharacteristics {
    id: string;
    sectorId: number;
    categoryPath: string;
    categoryName: string;
    status: string;
    aspects: {
        product_types: { data: any[]; hasData: boolean; count: number };
        brand_tiers: { data: Record<string, string[]>; hasData: boolean; tierCount: number };
        use_cases: { data: any[]; hasData: boolean; count: number };
        premium_features: { data: any[]; hasData: boolean; count: number };
        personas: { data: any[]; hasData: boolean; count: number };
        tier_vocabulary: { data: Record<string, { use: string[]; avoid: string[] }>; hasData: boolean; tierCount: number };
        price_tiers: { data: Record<string, PriceTier>; hasData: boolean; tierCount: number };
        spec_semantics: { data: Record<string, any>; hasData: boolean; specCount: number };
        measurement_units: { data: Record<string, any>; hasData: boolean; unitCount: number };
        validation_rules: { data: Record<string, any>; hasData: boolean };
    };
    freshness: { status: string; message: string; daysOld?: number };
    lastResearchedAt: string | null;
    researchCostUsd: number | null;   // null = researched but the cost could not be measured (task-px4ret1um8)
    // Grounding: catalogue products the research was based on. null = not recorded; 0 = the
    // personas were invented from general knowledge, not the catalogue (task-em3cnl32tq).
    sampleProductCount: number | null;
}

export default function CategoryResearch() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { selectedSite } = useSite();

    const [categoryName, setCategoryName] = useState(searchParams.get('category') || '');
    /**
     * Ruling (v): the catalogue roots of the selected site — the ONLY separator-free names a write may carry.
     * null = not loaded (a bare name is refused and told so); an empty Set = loaded, no roots.
     */
    const [catalogueRoots, setCatalogueRoots] = useState<ReadonlySet<string> | null>(null);
    const [researching, setResearching] = useState(false);
    const [loading, setLoading] = useState(false);
    const [perceptions, setPerceptions] = useState<PerceivedAttribute[]>([]);
    const [sessions, setSessions] = useState<ResearchSession[]>([]);
    const [availableCategories, setAvailableCategories] = useState<Set<string>>(new Set());
    const [error, setError] = useState<string | null>(null);
    const [selectedPerception, setSelectedPerception] = useState<PerceivedAttribute | null>(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [attributeIntelligence, setAttributeIntelligence] = useState<AttributeIntelligence[]>([]);
    const [researchingIntelligence, setResearchingIntelligence] = useState(false);
    const [characteristics, setCharacteristics] = useState<CategoryCharacteristics | null>(null);
    /**
     * ⛔ THE CATEGORY THIS PAGE IS CURRENTLY ABOUT, readable from inside an in-flight closure.
     * Clearing on change is not sufficient on its own: loadCharacteristics captures the path,
     * awaits, then sets state UNCONDITIONALLY, so a response for an EARLIER category that resolves
     * LAST overwrites a newer one. The panel would then describe row A while every write on the
     * page is addressed to row B — the same divergence the clear was added to close, re-entered
     * through response ordering. Same guard shape as CsQualityBridge's currentSiteIdRef.
     */
    const currentRequestKeyRef = useRef<string>('');
    const [loadingCharacteristics, setLoadingCharacteristics] = useState(false);
    /**
     * ⛔ "UNKNOWN" AND "CONFIRMED ABSENT" ARE NOT THE SAME STATE, AND `characteristics === null`
     * CANNOT TELL THEM APART. Both read as null:
     *   - a load is pending, or sector resolution is pending  ⇒ we do not know what is there
     *   - the fetch COMPLETED and this category genuinely has no row yet ⇒ we know: nothing
     * ⇒ Gating the destructive rebuild on `!characteristics` disabled the BOOTSTRAP path too, so
     * an operator following the page's own empty-state instruction — "Click Refresh All to
     * research category characteristics" — found the button dead. A guard that also blocks the
     * only way to create the thing is not a guard, it is a dead end. (codex leg-1 BLOCKING.)
     * ⇒ TRUE only once a fetch for the CURRENT key has resolved. An error leaves it FALSE, because
     * a failed read is still an unknown row.
     */
    const [characteristicsKnown, setCharacteristicsKnown] = useState(false);
    /** Did the last load for the current key FAIL? Distinguishes "could not read" from "still resolving". */
    const [characteristicsError, setCharacteristicsError] = useState(false);
    const [refreshingAspect, setRefreshingAspect] = useState<string | null>(null);
    // Guards the persona re-research when there are curated weights to lose (task-htf7zulfpv).
    const [confirmPersonaRefresh, setConfirmPersonaRefresh] = useState(false);
    const [confirmRefreshAll, setConfirmRefreshAll] = useState(false);
    /** ⛔ What this session knows it wrote — see shouldConfirmRefresh. Cleared per category. */
    const [weightsJustWritten, setWeightsJustWritten] = useState(false);
    const [resolvedSectorId, setResolvedSectorId] = useState<number | null>(null);
    /**
     * ⛔ WHICH DESTRUCTIVE REBUILD IS AWAITING CONFIRMATION. Every one of these controls used to run
     * on the click. They REPLACE stored research — including the buyer personas and any weighting
     * tuned by hand — and the result looks valid afterwards, so nothing reports the loss.
     * 'all' is Re-Research All; anything else is the single aspect being rebuilt.
     */
    const [pendingRefresh, setPendingRefresh] = useState<string | null>(null);
    const [sectorResolved, setSectorResolved] = useState(false);
    /**
     * ⛔⛔ TRUE WHEN THE SECTOR ON SCREEN IS A FALLBACK FROM A FAILED MULTI-SECTOR LOOKUP, not a
     * resolution. Reads may use a guess; a rebuild may not. See writeSectorId.
     */
    const [sectorIsGuess, setSectorIsGuess] = useState(false);
    /**
     * ⛔⛔ WHICH CATEGORY THE IN-FLIGHT SECTOR RESOLUTION IS FOR. Sector resolution is ASYNC and it
     * used to leave the PREVIOUS category's sector in state while the new one resolved, then apply
     * its answer unconditionally. ⇒ The characteristics load fires on `resolvedSectorId && categoryName`,
     * so in that window it fetched (OLD SECTOR, NEW PATH) and marked that row KNOWN — after which
     * the repair button and the guarded refreshes were perfectly self-consistent ABOUT THE WRONG
     * ROW, and the write is paid and keyed on exactly that pair. (codex BOTH legs, blocking.)
     */
    const sectorResolveKeyRef = useRef<string>('');

    // ⛔ The write-latch is per CATEGORY. Without this it would stay armed after navigating to a
    // different category, confirming on a row where nothing was written — which trains the operator
    // to click through, and a guard clicked through is not a guard.
    // ⚠️ THIRD KNOWN MEMBER OF task-87537g8s9a — see the collected note at the
    // PersonaRefreshConfirmDialog mounts below, which lists all three.
    // THIS FILE KEYS THE LATCH ON `categoryName` (free-text INPUT state). The admin copy keys the
    // same latch on `category?.categoryPath` (the FETCHED ROW's identity). ⇒ So here, transient
    // typing DISARMS the latch, and disarming is the UNSAFE direction: the latch exists to make
    // the guard fire when a repair has landed but the reload has not.
    // ⚠️ BOUNDED, NOT DISMISSED: I could not construct a live path to an unconfirmed destructive
    // refresh, because changing `categoryName` now also clears `characteristics`, so the dialogs
    // cannot render mid-typing, and the refetch that follows re-arms the guard via
    // weightedCountOf on its own. Left as-is because RE-KEYING A LATCH IS GUARD SEMANTICS and
    // belongs in that row's decision, not in a drive-by edit.
    useEffect(() => { setWeightsJustWritten(false); }, [categoryName]);

    // Resolve the correct sector for the current category
    // This ensures research is stored in the same sector AEO will read from
    useEffect(() => {
        // ⚠️ Keyed on the category AND the candidate sectors — sector_ids is sent to the route and
        // changes the answer, so the name alone cannot tell two in-flight lookups apart. Under-keyed
        // BEFORE this PR too; see sectorResolveKey.ts. No viewedSector: this surface has none, and a
        // default nobody chose is not one.
        const requestedCategory = categoryName;
        const requestedKey = sectorResolveKey({
            category: requestedCategory,
            candidateSectors: selectedSite?.sector_ids,
        });
        sectorResolveKeyRef.current = requestedKey;
        // ⛔ CLEAR FIRST. Until this resolves we do not know which sector this category belongs to,
        // and a stale truthy value is what let the loader run against the previous one. Nulling it
        // sends the load effect down its sector-pending branch, which clears the row and says so.
        setResolvedSectorId(null);
        setSectorResolved(false);

        async function resolveSector() {
            // If site has multiple sectors and we have a category, resolve the correct one
            if (selectedSite?.sector_ids && selectedSite.sector_ids.length > 1 && categoryName) {
                try {
                    const sectorIds = selectedSite.sector_ids.join(',');
                    const response = await authFetch(
                        `/category-characteristics/resolve-sector?sectorIds=${sectorIds}&categoryName=${encodeURIComponent(categoryName)}`
                    );
                    const data = await response.json();

                    // ⛔ DISCARD A RESOLUTION THE PAGE HAS MOVED ON FROM — otherwise the answer for
                    // the previous category lands last and becomes the sector for this one.
                    if (sectorResolveKeyRef.current !== requestedKey) return;
                    // ⛔⛔ `data.success` IS TRUE FOR A GUESS. A resolved:false response still carries
                    // a sectorId — the first candidate — so checking success alone reads a fallback
                    // as an answer. See resolvedSectorForWrite.
                    if (resolvedSectorForWrite(data) !== null) {
                        setResolvedSectorId(data.sectorId);
                        setSectorResolved(data.resolved);
                        console.log(`[CategoryResearch] Resolved sector: ${data.sectorId} (resolved=${data.resolved}) for "${categoryName}"`);
                        setSectorIsGuess(false);
                    } else {
                        // ⛔⛔ THE READ MAY FALL BACK. THE WRITE MAY NOT. Keeping the primary sector
                        // here is what lets the page keep LOADING, and that is fine — a read of the
                        // wrong row shows the wrong data and nothing is lost. But the same value
                        // was reaching the destructive handlers AND the new dialog, so the operator
                        // confirmed sector A while the category belonged to sector B.
                        // ⇒ Flagged as a GUESS. writeSectorId below refuses on it, exactly as the
                        // admin page does — same problem, same pattern, not a second one.
                        console.warn('[CategoryResearch] Sector resolution returned no sector for', categoryName, data);
                        setResolvedSectorId(selectedSite.sector_id || null);
                        setSectorResolved(false);
                        setSectorIsGuess(true);
                    }
                } catch (error) {
                    console.error('Failed to resolve sector:', error);
                    if (sectorResolveKeyRef.current !== requestedKey) return;
                    setResolvedSectorId(selectedSite.sector_id || null);
                    setSectorResolved(false);
                    setSectorIsGuess(true);
                }
            } else {
                // Single sector or no category - use primary sector.
                // ⛔ NOT A GUESS. With one sector there is nothing to resolve and no lookup is made,
                // so the primary sector is the only answer — the same distinction the admin page
                // draws. Marking this as a guess would disable every rebuild on single-sector sites.
                if (sectorResolveKeyRef.current !== requestedKey) return;
                setResolvedSectorId(selectedSite?.sector_id || null);
                setSectorResolved(false);
                setSectorIsGuess(false);
            }
        }

        resolveSector();
    }, [selectedSite?.sector_ids, selectedSite?.sector_id, categoryName]);

    // Load research sessions and available categories when site changes
    useEffect(() => {
        if (selectedSite?.sector_id) {
            loadSessions();
            // Clear perceptions when switching sites
            setPerceptions([]);
        }
    }, [selectedSite?.sector_id]);

    // B2 (kexnycgswp): the site's catalogue roots, for the bare-name rule. Reset to "not loaded" on every
    // site change so a root of the PREVIOUS site can never vouch for a name typed against this one.
    useEffect(() => {
        const siteId = selectedSite?.id || selectedSite?._id;
        setCatalogueRoots(null);
        if (!siteId) return;
        let cancelled = false;
        (async () => {
            try {
                const response = await authFetch(`/sites/${siteId}/product-categories`);
                const data = await response.json();
                if (cancelled) return;
                if (!response.ok || !Array.isArray(data?.categories)) {
                    // Loud, and left as "not loaded": a bare name typed now is refused with that reason.
                    console.error('[CategoryResearch] product categories unavailable; bare category names will be refused:', response.status, data?.error);
                    return;
                }
                setCatalogueRoots(catalogueRootsFrom(data.categories.map((c: { name?: string }) => c.name)));
            } catch (err: any) {
                if (!cancelled) console.error('[CategoryResearch] product categories request failed; bare category names will be refused:', err?.message || err);
            }
        })();
        return () => { cancelled = true; };
    }, [selectedSite?.id, selectedSite?._id]);

    // Load perceptions, attribute intelligence, and characteristics when resolved sector or category changes
    useEffect(() => {
        // Wait for sector resolution before loading
        if (resolvedSectorId && categoryName) {
            // ⛔⛔ CLEAR BEFORE LOADING, OR THE PAGE DESCRIBES ONE ROW WHILE ADDRESSING ANOTHER.
            // `categoryName` is FREE-TEXT INPUT STATE (:655 is a raw onChange), while
            // `characteristics` is only ever set from a fetch response. This effect used to clear
            // solely on the empty branch, so from the FIRST KEYSTROKE until the fetch resolved the
            // page still held the PREVIOUS category's characteristics — and every intermediate
            // keystroke is a syntactically valid category path, so the window opens constantly
            // rather than only on a deliberate switch.
            // ⇒ PersonaRepairButton renders on `hasPersonas` and shows `personas`, BOTH derived
            // from `characteristics`, while its write was addressed by `categoryName`. The operator
            // would confirm against a persona list belonging to a DIFFERENT row than the one Apply
            // POSTs to — and it is a paid write keyed on (sector, category_path). The weights-to-lose
            // count, the latch and the four-field success contract were all correct about the wrong
            // row. (codex leg-1 BLOCKING on #4753.)
            // ⚠️ Yes, this blanks the panel while typing. That is the intended cost: a confirm
            // dialog exists so a human can CHECK, and showing them a stale row defeats the whole
            // control. Nothing is better than wrong here.
            // ⚠️ THE SAME EXPRESSION loadCharacteristics USES, deliberately. If the two sides
            // computed the sector differently the keys could never match and EVERY response would
            // be discarded — a guard that silently drops all data looks exactly like a backend
            // that returns none.
            currentRequestKeyRef.current = `${resolvedSectorId || selectedSite?.sector_id}::${categoryName}`;
            setCharacteristics(null);
            setCharacteristicsKnown(false);
            setCharacteristicsError(false);
            loadPerceptions();
            loadAttributeIntelligence();
            loadCharacteristics();
        } else if (categoryName) {
            // ⛔ CATEGORY CHOSEN BUT SECTOR NOT RESOLVED YET — this branch did not exist, so the
            // effect fell through and did NOTHING while resolution was in flight, leaving the
            // PREVIOUS category's characteristics on screen for the whole window. Same divergence,
            // reached by waiting rather than by typing. Clear and refuse to key anything until the
            // sector is known: a load started now would be addressed to the wrong sector anyway.
            currentRequestKeyRef.current = '';
            setCharacteristics(null);
            setCharacteristicsKnown(false);
            setCharacteristicsError(false);
        } else if (!categoryName) {
            currentRequestKeyRef.current = '';
            // Clear data if category is cleared
            setPerceptions([]);
            setAttributeIntelligence([]);
            setCharacteristics(null);
        }
    }, [categoryName, resolvedSectorId]);

    async function loadSessions() {
        try {
            const response = await authFetch(`/category-research/${selectedSite.sector_id}/sessions`);
            const data = await response.json();
            if (data.success) {
                setSessions(data.sessions);
                // Extract unique category names from completed sessions
                const categories = new Set<string>();
                data.sessions.forEach((session: ResearchSession) => {
                    if (session.status === 'completed' && session.category_name) {
                        categories.add(session.category_name);
                    }
                });
                setAvailableCategories(categories);
            }
        } catch (err: any) {
            console.error('Error loading sessions:', err);
        }
    }

    async function loadPerceptions() {
        // Use resolved sector for category-level data
        const sectorId = resolvedSectorId || selectedSite.sector_id;
        if (!sectorId) return;

        try {
            setLoading(true);
            setError(null);
            const response = await authFetch(
                `/category-research/${sectorId}/perceptions/${encodeURIComponent(categoryName)}`
            );
            const data = await response.json();

            if (data.success) {
                setPerceptions(data.perceptions);
            } else {
                setError(data.error || 'Failed to load perceptions');
            }
        } catch (err: any) {
            console.error('Error loading perceptions:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }

    async function loadAttributeIntelligence() {
        // Use resolved sector for category-level data
        const sectorId = resolvedSectorId || selectedSite.sector_id;
        if (!sectorId) return;

        try {
            const siteId = selectedSite.id || selectedSite._id;
            const response = await authFetch(
                `/category-research/${sectorId}/attribute-intelligence/${siteId}?categoryName=${encodeURIComponent(categoryName)}`
            );
            const data = await response.json();

            if (data.success) {
                setAttributeIntelligence(data.intelligence);
            }
        } catch (err: any) {
            console.error('Error loading attribute intelligence:', err);
        }
    }

    async function loadCharacteristics() {
        if (!categoryName) return;

        // Use resolved sector for category-level data
        const sectorId = resolvedSectorId || selectedSite.sector_id;
        if (!sectorId) return;

        // ⛔⛔ THE REQUEST KEY IS (SECTOR, PATH). PATH ALONE IS NOT IDENTITY.
        // The first version of this guard tracked the path only. ⇒ On a MULTI-SECTOR SITE,
        // switching category starts a load under the PREVIOUS resolvedSectorId (sector resolution
        // is async and this effect re-runs when it lands), so two loads race for the SAME path
        // under DIFFERENT sectors. If the old-sector response resolved last, the path check passed
        // — the paths are identical — and the panel showed SECTOR A's personas while the button
        // POSTed to SECTOR B. That is this PR's own hazard, reproduced through the axis the guard
        // was not watching. (codex leg-1 BLOCKING at f055c1517.)
        // 🔑 The system presents category_path as the identifier everywhere and silently requires
        // the PAIR — the same trap has now bitten three different surfaces today.
        // Declared OUTSIDE the try so the catch can compare against it too — a stale request that
        // FAILS must not blank the row the page has since moved to.
        const categoryPath = categoryName;
        const requestKey = `${sectorId}::${categoryPath}`;

        try {
            setLoadingCharacteristics(true);
            const response = await authFetch(
                `/category-characteristics/${sectorId}/category/${encodeURIComponent(categoryPath)}`
            );
            const data = await response.json();

            // ⛔ DISCARD A RESPONSE THE PAGE HAS MOVED ON FROM. Without this, a fetch for an
            // earlier category that resolves LAST wins, and the panel describes a row that no
            // write on this page is addressed to.
            if (currentRequestKeyRef.current !== requestKey) return;

            if (data.success && data.data) {
                setCharacteristics(data.data);
            } else {
                setCharacteristics(null);
            }
            // ⇒ Either way the fetch RESOLVED for the current key, so the row is now known —
            // including "known to be absent", which is what the bootstrap path needs.
            setCharacteristicsKnown(true);
        } catch (err: any) {
            console.error('Error loading characteristics:', err);
            // Same guard: a STALE request failing must not blank the row the page has moved to.
            if (currentRequestKeyRef.current !== requestKey) return;
            setCharacteristics(null);
            setCharacteristicsError(true);
        } finally {
            // ⛔⛔ A `return` INSIDE try/catch STILL RUNS finally — SO THE STALE-REQUEST GUARDS
            // ABOVE DO NOT SKIP THIS. Without the check, an OLD request that correctly discarded
            // its own response would still clear the loading flag while the NEW fetch is in flight
            // and `characteristics` is null.
            // ⇒ THAT RE-OPENED THE HOLE THIS PR EXISTS TO CLOSE: with loading false and
            // characteristics null, storedPersonas is [] and shouldConfirmRefresh([], false)
            // returns FALSE, so Re-Research All on a category that DOES carry curated weights runs
            // WITHOUT the confirmation. My own guard created the window. (codex leg-2 BLOCKING.)
            if (currentRequestKeyRef.current === requestKey) setLoadingCharacteristics(false);
        }
    }

    async function refreshCharacteristicsAspect(aspect: string) {
        if (!categoryName) return;

        // ⛔ THE WRITE SECTOR, NOT THE READ ONE. Refuses rather than guessing — see writeSectorId.
        const sectorId = writeSectorId;
        if (!sectorId) {
            alert(`Cannot re-research "${categoryName}" yet: we could not work out which sector it belongs to, and this site has more than one. Nothing was changed.`);
            return;
        }
        // B2 (kexnycgswp): the destructive refresh is a WRITE keyed by whatever was typed; a bare leaf is refused.
        const writeKey = categoryWriteKey(categoryName, catalogueRoots);
        if (!writeKey.ok) {
            alert(writeKey.reason);
            return;
        }

        try {
            setRefreshingAspect(aspect);
            const categoryPath = writeKey.key;
            const response = await authFetch(
                `/category-characteristics/${sectorId}/category/${encodeURIComponent(categoryPath)}/refresh/${aspect}`,
                { method: 'POST' }
            );
            const data = await response.json();

            if (data.success) {
                await loadCharacteristics();
            } else {
                alert(`Failed to refresh ${aspect}: ${data.error}`);
            }
        } catch (err: any) {
            console.error(`Error refreshing ${aspect}:`, err);
            alert(`Failed to refresh ${aspect}: ${err.message}`);
        } finally {
            setRefreshingAspect(null);
        }
    }

    async function refreshAllCharacteristics() {
        if (!categoryName) return;

        // ⛔ THE WRITE SECTOR, NOT THE READ ONE. Refuses rather than guessing — see writeSectorId.
        const sectorId = writeSectorId;
        if (!sectorId) {
            alert(`Cannot re-research "${categoryName}" yet: we could not work out which sector it belongs to, and this site has more than one. Nothing was changed.`);
            return;
        }
        // B2 (kexnycgswp): the destructive refresh is a WRITE keyed by whatever was typed; a bare leaf is refused.
        const writeKey = categoryWriteKey(categoryName, catalogueRoots);
        if (!writeKey.ok) {
            alert(writeKey.reason);
            return;
        }

        const categoryPath = writeKey.key;
        // Same composite identity as loadCharacteristics — see the note there on why the pair and
        // not the path.
        const requestKey = `${sectorId}::${categoryPath}`;

        try {
            setLoadingCharacteristics(true);
            const response = await authFetch(
                `/category-characteristics/${sectorId}/category/${encodeURIComponent(categoryPath)}/refresh`,
                { method: 'POST' }
            );
            const data = await response.json();

            if (data.success) {
                await loadCharacteristics();
                alert('Category characteristics refreshed successfully!');
            } else {
                alert(`Failed to refresh: ${data.error}`);
            }
        } catch (err: any) {
            console.error('Error refreshing characteristics:', err);
            alert(`Failed to refresh: ${err.message}`);
        } finally {
            // ⛔ SAME RULE AS loadCharacteristics, AND MY OWN TEST CAUGHT THIS SECOND SITE. The
            // reviewer named the load path; this is the WRITE path, and it has the same exposure —
            // if the operator switches category while a refresh is in flight, clearing the flag
            // unconditionally reports "loaded" for a row this operation is no longer about, and
            // an unknown row then reads as an empty one.
            if (currentRequestKeyRef.current === requestKey) setLoadingCharacteristics(false);
        }
    }

    async function handleResearchIntelligence() {
        if (!categoryName.trim()) {
            alert('Please enter a category name');
            return;
        }
        // B2 (kexnycgswp): a bare leaf typed here used to be POSTed as-is and created a leaf row; refused now.
        const writeKey = categoryWriteKey(categoryName, catalogueRoots);
        if (!writeKey.ok) {
            alert(writeKey.reason);
            return;
        }

        // Use resolved sector for category-level data
        const sectorId = resolvedSectorId || selectedSite.sector_id;
        if (!sectorId) {
            alert('No sector resolved for this category');
            return;
        }

        try {
            setResearchingIntelligence(true);
            const siteId = selectedSite.id || selectedSite._id;

            const response = await authFetch(`/category-research/${sectorId}/attribute-intelligence`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    categoryName: writeKey.key,
                    siteId
                })
            });

            const data = await response.json();

            if (data.success) {
                alert(`✓ Attribute Intelligence research completed!\n\n${data.attributesResearched} attribute interpretations generated.`);
                await loadAttributeIntelligence();
            } else {
                throw new Error(data.error || 'Research failed');
            }
        } catch (err: any) {
            console.error('Error researching attribute intelligence:', err);
            alert(`Research failed: ${err.message}`);
        } finally {
            setResearchingIntelligence(false);
        }
    }

    async function handleResearch() {
        if (!categoryName.trim()) {
            alert('Please enter a category name');
            return;
        }
        // B2 (kexnycgswp): a bare leaf typed here used to be POSTed as-is and created a leaf row; refused now.
        const writeKey = categoryWriteKey(categoryName, catalogueRoots);
        if (!writeKey.ok) {
            alert(writeKey.reason);
            return;
        }

        // Use resolved sector for category-level data
        const sectorId = resolvedSectorId || selectedSite.sector_id;
        if (!sectorId) {
            alert('No sector resolved for this category');
            return;
        }

        try {
            setResearching(true);
            setError(null);

            const response = await authFetch(`/category-research/${sectorId}/research`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ categoryName: writeKey.key, options: { categoryPath: writeKey.key } })
            });

            const data = await response.json();

            if (data.success) {
                alert(`✓ Research completed!\n\n${data.attributeCount} perceived attributes identified.`);
                await loadPerceptions();
                await loadSessions();
            } else {
                throw new Error(data.error || 'Research failed');
            }
        } catch (err: any) {
            console.error('Error researching category:', err);
            setError(err.message);
            alert(`Research failed: ${err.message}`);
        } finally {
            setResearching(false);
        }
    }

    async function handleDelete(perceptionId: string) {
        if (!confirm('Are you sure you want to delete this perceived attribute?')) {
            return;
        }

        // Use resolved sector for category-level data
        const sectorId = resolvedSectorId || selectedSite.sector_id;
        if (!sectorId) return;

        try {
            await authFetch(`/category-research/${sectorId}/perceptions/${perceptionId}`, {
                method: 'DELETE'
            });
            await loadPerceptions();
        } catch (err: any) {
            console.error('Error deleting perception:', err);
            alert(`Delete failed: ${err.message}`);
        }
    }

    function openEditDialog(perception: PerceivedAttribute) {
        setSelectedPerception(perception);
        setEditDialogOpen(true);
    }

    async function handleSaveEdit() {
        if (!selectedPerception) return;

        // Use resolved sector for category-level data
        const sectorId = resolvedSectorId || selectedSite.sector_id;
        if (!sectorId) return;

        try {
            await authFetch(`/category-research/${sectorId}/perceptions/${selectedPerception.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    attribute_name: selectedPerception.attribute_name,
                    customer_description: selectedPerception.customer_description,
                    common_phrases: selectedPerception.common_phrases,
                    technical_features: selectedPerception.technical_features
                })
            });

            setEditDialogOpen(false);
            setSelectedPerception(null);
            await loadPerceptions();
        } catch (err: any) {
            console.error('Error updating perception:', err);
            alert(`Update failed: ${err.message}`);
        }
    }

    function getStatusBadge(status: string) {
        switch (status) {
            case 'completed':
                return <Badge variant="default" className="flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Completed</Badge>;
            case 'failed':
                return <Badge variant="destructive" className="flex items-center gap-1"><XCircle className="w-3 h-3" /> Failed</Badge>;
            case 'in_progress':
                return <Badge variant="secondary" className="flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> In Progress</Badge>;
            default:
                return <Badge variant="outline">{status}</Badge>;
        }
    }

    function getAttributeTypeBadge(type: string) {
        const colors: Record<string, string> = {
            functional: 'bg-blue-100 text-blue-800 border-blue-200',
            use_case: 'bg-green-100 text-green-800 border-green-200',
            sustainability: 'bg-emerald-100 text-emerald-800 border-emerald-200',
            maintenance: 'bg-orange-100 text-orange-800 border-orange-200',
            trust: 'bg-purple-100 text-purple-800 border-purple-200',
            value: 'bg-yellow-100 text-yellow-800 border-yellow-200',
        };
        const labels: Record<string, string> = {
            functional: 'Functional',
            use_case: 'Use Case',
            sustainability: 'Sustainability',
            maintenance: 'Maintenance',
            trust: 'Trust',
            value: 'Value',
        };
        return (
            <Badge className={`${colors[type] || 'bg-gray-100 text-gray-800'} border`}>
                {labels[type] || type}
            </Badge>
        );
    }

    function getSentimentIcon(sentiment: string) {
        switch (sentiment) {
            case 'positive':
                return <ThumbsUp className="w-3 h-3 text-green-600" />;
            case 'negative':
                return <ThumbsDown className="w-3 h-3 text-red-600" />;
            case 'question':
                return <HelpCircle className="w-3 h-3 text-blue-600" />;
            default:
                return <MessageSquare className="w-3 h-3 text-gray-600" />;
        }
    }

    function getSentimentBadgeClass(sentiment: string) {
        switch (sentiment) {
            case 'positive':
                return 'bg-green-50 text-green-700 border-green-200';
            case 'negative':
                return 'bg-red-50 text-red-700 border-red-200';
            case 'question':
                return 'bg-blue-50 text-blue-700 border-blue-200';
            default:
                return 'bg-gray-50 text-gray-700 border-gray-200';
        }
    }

    if (!selectedSite) {
        return (
            <div className="p-8">
                <div className="flex items-center gap-2 text-yellow-600">
                    <AlertCircle className="w-5 h-5" />
                    <p>Please select a site first</p>
                </div>
            </div>
        );
    }

    /** ⚠️ ONE SOURCE OF TRUTH for the guards' predicate. The Re-Research All button and the
     *  persona section live in different scopes and were each deriving this list separately —
     *  which is how two guards on the same predicate come to disagree about whether to fire. */
    /**
     * ⛔⛔ THE SECTOR A DESTRUCTIVE REBUILD MAY USE. undefined = NOT KNOWN, and unknown refuses.
     * This is the admin page's resolvedWriteSectorId, mirrored rather than reinvented: reads keep
     * their fallback because a read of the wrong row loses nothing, and writes refuse because a
     * write to the wrong (sector, path) pair cannot be undone. Same problem, one pattern.
     */
    const writeSectorId: number | undefined =
        sectorIsGuess ? undefined : (resolvedSectorId ?? selectedSite?.sector_id ?? undefined);

    const storedPersonas: PersonaLike[] = (characteristics?.aspects?.personas?.data || []) as PersonaLike[];

    return (
        <div className="p-8">
            {/* Header */}
            <div className="mb-6">
                <div className="flex items-center gap-2 mb-2">
                    <Button onClick={() => navigate(-1)} variant="ghost" size="sm">
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back
                    </Button>
                </div>
                <h1 className="text-3xl font-bold">Category Perception Research</h1>
                <p className="text-gray-600">
                    Research customer-perceived attributes that bridge technical specs and customer needs
                </p>
            </div>

            {/*
              * ⛔⛔ A SILENT DISABLE IS NOT A REFUSAL THE OPERATOR CAN ACT ON (task-2by6ldlmau, 2b).
              * When the sector cannot be determined, writeSectorId goes undefined and every
              * destructive control below is `disabled` — which looks identical to "nothing to do
              * here". The handlers DO explain themselves (:489, :521), but only if you can click
              * them, and you cannot. The same conflation cost an operator twenty minutes on the
              * admin page's spinner today.
              * ⇒ Say what could not be determined and what would fix it. Reads are unaffected and
              * continue against the site's primary sector, so this is not an error state.
              */}
            {sectorIsGuess && (
                <div className="mb-6 flex items-start gap-3 rounded-md border border-amber-300 bg-amber-50 p-4">
                    <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
                    <div className="text-sm text-amber-900">
                        <p className="font-semibold">
                            Rebuild actions are disabled: we could not work out which sector this category belongs to.
                        </p>
                        <p className="mt-1">
                            This site has more than one sector, and “{categoryName}” did not match a researched
                            category in any of them. Matching needs the category’s full path (for example
                            <span className="font-mono"> Sporting Goods &gt; … &gt; Bicycle Tires</span>), not just its
                            name. You are still seeing this page’s data, read from the site’s primary sector — only
                            actions that would write are blocked, because writing to the wrong sector cannot be undone.
                        </p>
                    </div>
                </div>
            )}

            {/* Research Form */}
            <Card className="mb-6">
                <CardHeader>
                    <CardTitle>Research a Product Category</CardTitle>
                    <CardDescription>
                        Identify the perceived attributes customers care about when choosing products in this category
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="space-y-4">
                        {/* Previously Researched Categories */}
                        {availableCategories.size > 0 && (
                            <div>
                                <Label className="text-xs font-semibold text-gray-600">Previously Researched Categories</Label>
                                <div className="flex flex-wrap gap-2 mt-2">
                                    {Array.from(availableCategories).map((cat) => (
                                        <Badge
                                            key={cat}
                                            variant={categoryName === cat ? "default" : "outline"}
                                            className="cursor-pointer hover:bg-primary hover:text-white transition-colors"
                                            onClick={() => setCategoryName(cat)}
                                        >
                                            {cat}
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Research Input */}
                        <div className="flex gap-4">
                            <div className="flex-1">
                                <Label htmlFor="category">Product Category</Label>
                                <Input
                                    id="category"
                                    placeholder="e.g., Bike wheels and tires"
                                    value={categoryName}
                                    onChange={(e) => setCategoryName(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handleResearch();
                                        }
                                    }}
                                />
                            </div>
                            <div className="flex items-end">
                                <Button onClick={handleResearch} disabled={researching || !categoryName.trim()}>
                                    {researching ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Researching...
                                        </>
                                    ) : (
                                        <>
                                            <Sparkles className="w-4 h-4 mr-2" />
                                            Research Category
                                        </>
                                    )}
                                </Button>
                            </div>
                        </div>

                        {error && (
                            <div className="p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                                {error}
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Tabs */}
            <Tabs defaultValue="perceptions" className="space-y-4">
                <TabsList>
                    <TabsTrigger value="perceptions">
                        Perceived Benefits ({perceptions.length})
                    </TabsTrigger>
                    <TabsTrigger value="intelligence">
                        Attribute Intelligence ({attributeIntelligence.length})
                    </TabsTrigger>
                    <TabsTrigger value="characteristics">
                        Category Characteristics {characteristics?.aspects?.price_tiers?.hasData ? '✓' : ''}
                    </TabsTrigger>
                    <TabsTrigger value="sessions">
                        Research Sessions ({sessions.length})
                    </TabsTrigger>
                </TabsList>

                {/* Perceived Attributes Tab */}
                <TabsContent value="perceptions">
                    <Card>
                        <CardHeader>
                            <CardTitle>Perceived Attributes for "{categoryName || 'Category'}"</CardTitle>
                            <CardDescription>
                                Customer-facing attributes that matter when choosing products
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            {loading ? (
                                <div className="flex items-center justify-center py-8">
                                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                                </div>
                            ) : perceptions.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">
                                    <Search className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                                    <p>No perceived attributes found for this category</p>
                                    <p className="text-sm">Research a category to generate insights</p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {perceptions.map((perception) => (
                                        <Card key={perception.id} className="border border-gray-200">
                                            <CardHeader>
                                                <div className="flex items-start justify-between">
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <CardTitle className="text-lg">{perception.attribute_name}</CardTitle>
                                                            {getAttributeTypeBadge(perception.attribute_type || 'functional')}
                                                            {perception.research_quality_score && (
                                                                <Badge variant="outline" className="text-xs">
                                                                    Quality: {(perception.research_quality_score * 100).toFixed(0)}%
                                                                </Badge>
                                                            )}
                                                        </div>
                                                        <CardDescription className="mt-2">
                                                            {perception.customer_description}
                                                        </CardDescription>
                                                    </div>
                                                    <div className="flex gap-2">
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => openEditDialog(perception)}
                                                        >
                                                            <Edit className="w-4 h-4" />
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleDelete(perception.id)}
                                                        >
                                                            <Trash2 className="w-4 h-4 text-red-600" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            </CardHeader>
                                            <CardContent>
                                                <div className="space-y-4">
                                                    {/* Customer Voice Samples */}
                                                    {perception.voice_samples && perception.voice_samples.length > 0 && (
                                                        <div>
                                                            <Label className="text-xs font-semibold text-gray-600 flex items-center gap-1">
                                                                <MessageSquare className="w-3 h-3" />
                                                                Customer Voice Samples
                                                            </Label>
                                                            <div className="space-y-2 mt-2">
                                                                {perception.voice_samples.slice(0, 5).map((sample, i) => (
                                                                    <div
                                                                        key={i}
                                                                        className={`flex items-start gap-2 p-2 rounded border ${getSentimentBadgeClass(sample.sentiment)}`}
                                                                    >
                                                                        {getSentimentIcon(sample.sentiment)}
                                                                        <span className="text-sm flex-1">"{sample.phrase}"</span>
                                                                        {sample.confidence_score && (
                                                                            <span className="text-xs opacity-70">
                                                                                {(sample.confidence_score * 100).toFixed(0)}%
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                                {perception.voice_samples.length > 5 && (
                                                                    <div className="text-xs text-gray-500 text-center">
                                                                        +{perception.voice_samples.length - 5} more samples
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Common Customer Phrases */}
                                                    <div>
                                                        <Label className="text-xs font-semibold text-gray-600">Common Customer Phrases</Label>
                                                        <div className="flex flex-wrap gap-2 mt-2">
                                                            {perception.common_phrases?.map((phrase, i) => (
                                                                <Badge key={i} variant="secondary">{phrase}</Badge>
                                                            ))}
                                                        </div>
                                                    </div>

                                                    {/* Related Technical Features */}
                                                    <div>
                                                        <Label className="text-xs font-semibold text-gray-600">Related Technical Features</Label>
                                                        <div className="flex flex-wrap gap-2 mt-2">
                                                            {perception.technical_features?.map((feature, i) => (
                                                                <Badge key={i} variant="outline">{feature}</Badge>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Attribute Intelligence Tab */}
                <TabsContent value="intelligence">
                    <Card>
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <div>
                                    <CardTitle>Attribute Intelligence for "{categoryName || 'Category'}"</CardTitle>
                                    <CardDescription>
                                        Explains what technical spec values mean to customers (e.g., TPI=60 vs TPI=120)
                                    </CardDescription>
                                </div>
                                <Button
                                    onClick={handleResearchIntelligence}
                                    disabled={researchingIntelligence || !categoryName.trim() || perceptions.length === 0}
                                    variant="outline"
                                >
                                    {researchingIntelligence ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Researching...
                                        </>
                                    ) : (
                                        <>
                                            <Sparkles className="w-4 h-4 mr-2" />
                                            Research Attribute Values
                                        </>
                                    )}
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            {perceptions.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">
                                    <AlertCircle className="w-12 h-12 mx-auto mb-4 text-yellow-500" />
                                    <p className="font-medium">Research Perceived Benefits First</p>
                                    <p className="text-sm mt-1">
                                        Attribute intelligence is derived from technical features in perceived benefits.
                                        Research the category first to identify technical features.
                                    </p>
                                </div>
                            ) : attributeIntelligence.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">
                                    <Search className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                                    <p>No attribute intelligence found for this category</p>
                                    <p className="text-sm">Click "Research Attribute Values" to generate interpretations</p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {attributeIntelligence.map((intel) => (
                                        <Card key={intel.id} className="border border-gray-200">
                                            <CardHeader>
                                                <div className="flex items-center gap-2">
                                                    <CardTitle className="text-lg">{intel.title}</CardTitle>
                                                    <Badge variant="outline">{intel.scope_attribute_name}</Badge>
                                                </div>
                                                <CardDescription>{intel.knowledge_text}</CardDescription>
                                            </CardHeader>
                                            <CardContent>
                                                <div className="bg-gray-50 p-4 rounded-lg">
                                                    <Label className="text-xs font-semibold text-gray-600">Value Interpretations</Label>
                                                    <pre className="mt-2 text-sm whitespace-pre-wrap font-mono text-gray-700">
                                                        {intel.research_findings}
                                                    </pre>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Category Characteristics Tab */}
                <TabsContent value="characteristics">
                    <Card>
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <div>
                                    <CardTitle>Category Characteristics for "{categoryName || 'Category'}"</CardTitle>
                                    <CardDescription>
                                        AI-researched market data including price tiers, brand tiers, and spec semantics
                                    </CardDescription>
                                </div>
                                <Button
                                    /* ⛔⛔ WARN ALWAYS, ESCALATE WHEN WEIGHTS ARE AT STAKE. The
                                       predicate no longer decides WHETHER to warn — only HOW hard.
                                       It used to gate the warning entirely, so the common case (a
                                       category with personas and no hand-tuned weights) ran with no
                                       warning at all, and the predicate could not tell "nothing to
                                       lose" from "not loaded yet" — both are an empty list.
                                       ⇒ weights at stake: the type-REFRESH dialog. Otherwise: the
                                       plain warning. Never straight through. */
                                    onClick={() => {
                                        if (shouldConfirmRefresh(storedPersonas, weightsJustWritten)) setConfirmRefreshAll(true);
                                        else setPendingRefresh('all');
                                    }}
                                    /* ⛔ KEPT FROM #4753 AND STILL LOAD-BEARING: do not offer a
                                       destructive rebuild while the row is UNKNOWN.
                                       `loadingCharacteristics` alone is not enough — characteristics
                                       is also null while sector resolution is pending, and an
                                       unknown row must not be treated as an empty one. */
                                    /* ⛔ ...and an unknown write sector too — same reason as the aspect
                                       buttons below. */
                                    disabled={loadingCharacteristics || !categoryName.trim() || !characteristicsKnown || writeSectorId == null}
                                    variant="outline"
                                >
                                    {loadingCharacteristics ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Researching...
                                        </>
                                    ) : (
                                        <>
                                            <RefreshCw className="w-4 h-4 mr-2" />
                                            Refresh All
                                        </>
                                    )}
                                </Button>
                            </div>
                            {characteristics?.freshness && (
                                <div className="mt-2">
                                    <Badge variant={
                                        characteristics.freshness.status === 'fresh' ? 'default' :
                                        characteristics.freshness.status === 'stale' ? 'destructive' : 'secondary'
                                    }>
                                        {characteristics.freshness.message}
                                    </Badge>
                                    {(characteristics.researchCostUsd ?? 0) > 0 && (
                                        <span className="ml-2 text-xs text-muted-foreground">
                                            Research cost: ${(characteristics.researchCostUsd ?? 0).toFixed(4)}
                                        </span>
                                    )}
                                </div>
                            )}
                        </CardHeader>
                        <CardContent>
                            {loadingCharacteristics ? (
                                <div className="flex items-center justify-center py-8">
                                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                                </div>
                            ) : !characteristics ? (
                                /* ⛔ THIS BRANCH FIRES IN THREE DIFFERENT STATES AND USED TO GIVE THE SAME
                                   INSTRUCTION IN ALL OF THEM: fetch-completed-with-no-row (Refresh All is
                                   the right advice), load-FAILED, and sector-resolution-pending. In the
                                   last two the button is correctly disabled, so telling the operator to
                                   click it points them at a dead control — the same defect a reviewer
                                   blocked on for the confirmed-absent case, surviving in its neighbours.
                                   Say which state this actually is. */
                                <div className="text-center py-8 text-gray-500">
                                    <Search className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                                    {characteristicsError ? (
                                        <>
                                            <p>Could not load this category's characteristics</p>
                                            <p className="text-sm">Re-select the category to try again. Research is unavailable until it loads.</p>
                                        </>
                                    ) : !characteristicsKnown ? (
                                        <p>Checking this category...</p>
                                    ) : (
                                        <>
                                            <p>No characteristics found for this category</p>
                                            <p className="text-sm">Click "Refresh All" to research category characteristics</p>
                                        </>
                                    )}
                                </div>
                            ) : (
                                <div className="space-y-6">
                                    {/* Price Tiers Section */}
                                    <div className="border rounded-lg p-4">
                                        <div className="flex items-center justify-between mb-4">
                                            <div className="flex items-center gap-2">
                                                <DollarSign className="w-5 h-5 text-green-600" />
                                                <h3 className="font-semibold">Price Tiers</h3>
                                                {characteristics.aspects.price_tiers?.hasData ? (
                                                    <Badge variant="default" className="text-xs">Researched</Badge>
                                                ) : (
                                                    <Badge variant="outline" className="text-xs">Not researched</Badge>
                                                )}
                                            </div>
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => setPendingRefresh('price_tiers')}
                                                /* ⛔ DO NOT OPEN A "Replace" DIALOG FOR AN OPERATION GUARANTEED TO
                                                   REFUSE. The handler already fails closed on an unknown write
                                                   sector, so this is not data loss — but walking an operator
                                                   through a destructive confirmation that cannot succeed teaches
                                                   them the dialog is theatre. The admin page disables here; this
                                                   is its twin. */
                                                disabled={refreshingAspect === 'price_tiers' || writeSectorId == null}
                                            >
                                                {refreshingAspect === 'price_tiers' ? (
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                ) : (
                                                    <RefreshCw className="w-4 h-4" />
                                                )}
                                            </Button>
                                        </div>
                                        {characteristics.aspects.price_tiers?.hasData ? (
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                                {['entry', 'mid_range', 'premium', 'ultra_premium'].map((tier) => {
                                                    const tierData = characteristics.aspects.price_tiers.data[tier];
                                                    if (!tierData) return null;
                                                    return (
                                                        <div key={tier} className="bg-muted/50 rounded-lg p-3">
                                                            <div className="font-medium text-sm capitalize mb-1">
                                                                {tier.replace('_', ' ')}
                                                            </div>
                                                            <div className="text-lg font-bold text-primary">
                                                                {tierData.max ? `≤ €${tierData.max}` : 'No limit'}
                                                            </div>
                                                            {tierData.personas?.length > 0 && (
                                                                <div className="mt-2 flex flex-wrap gap-1">
                                                                    {tierData.personas.slice(0, 3).map((persona: string, i: number) => (
                                                                        <Badge key={i} variant="outline" className="text-xs">
                                                                            {persona.replace('_', ' ')}
                                                                        </Badge>
                                                                    ))}
                                                                    {tierData.personas.length > 3 && (
                                                                        <Badge variant="outline" className="text-xs">
                                                                            +{tierData.personas.length - 3}
                                                                        </Badge>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <p className="text-sm text-muted-foreground">
                                                No price tiers researched yet. Click refresh to research market-based price tiers.
                                            </p>
                                        )}
                                    </div>

                                    {/* Brand Tiers Section */}
                                    <div className="border rounded-lg p-4">
                                        <div className="flex items-center justify-between mb-4">
                                            <div className="flex items-center gap-2">
                                                <Tag className="w-5 h-5 text-blue-600" />
                                                <h3 className="font-semibold">Brand Tiers</h3>
                                                {characteristics.aspects.brand_tiers?.hasData ? (
                                                    <Badge variant="default" className="text-xs">Researched</Badge>
                                                ) : (
                                                    <Badge variant="outline" className="text-xs">Not researched</Badge>
                                                )}
                                            </div>
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => setPendingRefresh('brand_tiers')}
                                                /* ⛔ DO NOT OPEN A "Replace" DIALOG FOR AN OPERATION GUARANTEED TO
                                                   REFUSE. The handler already fails closed on an unknown write
                                                   sector, so this is not data loss — but walking an operator
                                                   through a destructive confirmation that cannot succeed teaches
                                                   them the dialog is theatre. The admin page disables here; this
                                                   is its twin. */
                                                disabled={refreshingAspect === 'brand_tiers' || writeSectorId == null}
                                            >
                                                {refreshingAspect === 'brand_tiers' ? (
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                ) : (
                                                    <RefreshCw className="w-4 h-4" />
                                                )}
                                            </Button>
                                        </div>
                                        {characteristics.aspects.brand_tiers?.hasData ? (
                                            <div className="grid grid-cols-3 gap-4">
                                                {['premium', 'mid', 'entry'].map((tier) => {
                                                    const brands = characteristics.aspects.brand_tiers.data[tier] || [];
                                                    return (
                                                        <div key={tier}>
                                                            <div className="font-medium text-sm capitalize mb-2">{tier}</div>
                                                            <div className="flex flex-wrap gap-1">
                                                                {brands.slice(0, 8).map((brand: string, i: number) => (
                                                                    <Badge key={i} variant="secondary" className="text-xs">
                                                                        {brand}
                                                                    </Badge>
                                                                ))}
                                                                {brands.length > 8 && (
                                                                    <Badge variant="outline" className="text-xs">
                                                                        +{brands.length - 8}
                                                                    </Badge>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <p className="text-sm text-muted-foreground">
                                                No brand tiers researched yet.
                                            </p>
                                        )}
                                    </div>

                                    {/* Personas Section — surfaces the fields that decide whether a
                                        persona actually WORKS: benefit_weights (the routing predicate),
                                        life_stage, and how many products the research was grounded on.
                                        task-em3cnl32tq. Display-only; no control here writes. */}
                                    {(() => {
                                        const personas: PersonaLike[] = characteristics.aspects.personas?.data || [];
                                        const hasPersonas = !!characteristics.aspects.personas?.hasData;
                                        const grounding = characteristics.sampleProductCount; // null | number
                                        return (
                                    <div className="border rounded-lg p-4">
                                        <div className="flex items-center justify-between mb-4">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <Users className="w-5 h-5 text-purple-600" />
                                                <h3 className="font-semibold">Buyer Personas</h3>
                                                {hasPersonas ? (
                                                    <PersonaHealthBadges
                                                        personas={personas}
                                                        count={characteristics.aspects.personas.count}
                                                        grounding={grounding}
                                                    />
                                                ) : (
                                                    <Badge variant="outline" className="text-xs">Not researched</Badge>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2">
                                                {/* THE REPAIR. Sits beside the control that undoes it, which is
                                                    deliberate: the operator who has just seen a red "no benefit
                                                    weights" badge needs the fix within reach of the badge. */}
                                                {hasPersonas && (
                                                    <PersonaRepairButton
                                                        /* ⛔ BOTH HALVES OF THE IDENTITY FROM THE SAME ROW. This took
                                                           sectorId from PAGE STATE and categoryPath from the FETCHED
                                                           ROW — a pair assembled from two sources, which is the same
                                                           defect as taking the path from the search box, one axis
                                                           over. The write is keyed on (sector, category_path) and the
                                                           row carries BOTH (routes/category-characteristics.js:172-173
                                                           returns sectorId and categoryPath from one record), so
                                                           there is no reason to source half of it from elsewhere. */
                                                        sectorId={characteristics.sectorId}
                                                        /* ⛔ ADDRESS THE WRITE TO THE ROW BEING DISPLAYED, NOT TO THE
                                                           SEARCH BOX. `personas` and `hasPersonas` above both come from
                                                           `characteristics`; taking the path from `categoryName` — free-text
                                                           input state — let the confirm dialog describe one row while Apply
                                                           POSTed to another, on a PAID write keyed on (sector, category_path).
                                                           Clearing on change and the stale-response guard close the window,
                                                           but sourcing the path from the same object as the data makes the
                                                           agreement STRUCTURAL rather than a timing coincidence. It is also
                                                           the stored path rather than whatever the human typed. */
                                                        categoryPath={characteristics.categoryPath}
                                                        personas={personas}
                                                        onApplied={async () => {
                                                            // ⛔ Latch before the await, same reason
                                                            // as the admin surface.
                                                            setWeightsJustWritten(true);
                                                            await loadCharacteristics();
                                                        }}
                                                    />
                                                )}
                                                {/* Labelled refresh — distinguishable from "Refresh All" without hovering.
                                                    Re-researches personas ONLY for this one category.
                                                    ⛔ GUARDED WHEN THERE ARE WEIGHTS TO LOSE. Unguarded, this button
                                                    silently discards the repair standing next to it — and the new
                                                    personas look valid, so nothing afterwards reports the loss. */}
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    /* ⛔⛔ WARN ALWAYS, ESCALATE ON WEIGHTS — see Refresh All above.
                                                       A rebuild destroys the PERSONAS whether or not anyone tuned
                                                       weights on them, and that loss is just as permanent. */
                                                    onClick={() => {
                                                        if (shouldConfirmRefresh(personas, weightsJustWritten)) setConfirmPersonaRefresh(true);
                                                        else setPendingRefresh('personas');
                                                    }}
                                                    disabled={refreshingAspect === 'personas' || writeSectorId == null}
                                                    title="Re-research the buyer personas for this category only (does not touch price tiers, brand tiers, etc.). This overwrites the existing personas."
                                                >
                                                    {refreshingAspect === 'personas' ? (
                                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                    ) : (
                                                        <RefreshCw className="w-4 h-4 mr-2" />
                                                    )}
                                                    Re-research personas
                                                </Button>
                                            </div>
                                        </div>
                                        {/* ⛔ The BLUE Re-Research All button, guarded on the same
                                            predicate. Mounted here rather than beside the button so
                                            both dialogs sit in one place and cannot drift apart.

                                            ⚠️ COLLECTED NOTE FOR task-87537g8s9a — THREE KNOWN
                                            MEMBERS OF ONE DECISION: which identifier do these
                                            controls key on, the FETCHED ROW or the INPUT BOX?
                                              1 + 2. The two dialog mounts immediately below.
                                              3. The latch-clearing effect at the top of this file,
                                                 keyed on `categoryName` here but on the row's
                                                 `categoryPath` in src/pages/admin/CategoryResearch.tsx.
                                            Three instances, one question — which is what makes the
                                            decision worth taking once rather than three times.

                                            ⛔ AND THE QUESTION HAS TWO AXES, NOT ONE. Identity here
                                            is the PAIR (sector, category_path), not the path. Both
                                            mounts below take categoryPath from `categoryName` AND
                                            sectorId from page state — self-consistent with their
                                            handlers, which use the same two, so they are not
                                            currently divergent. But if anyone re-points the path at
                                            the fetched row they MUST move the sector with it, or
                                            they will have built the exact defect fixed above on
                                            PersonaRepairButton: half the identity from the row and
                                            half from page state.
                                            ⚠️ The admin copy resolves its sector through
                                            getWriteSectorId() on purpose — that resolution IS what
                                            task-87537g8s9a is about, so it is deliberately not
                                            aligned with this file.

                                            ⚠️ KNOWN MEMBERS OF task-87537g8s9a — DO NOT "FIX" THESE
                                            IN ISOLATION. BOTH dialogs below take categoryPath from
                                            `categoryName` (free-text input state) while their
                                            persona list comes from `characteristics` (the fetched
                                            row). That mixing is exactly the shape of the blocking
                                            defect fixed on PersonaRepairButton above.
                                            ⇒ IT IS LEFT AS-IS DELIBERATELY. Their onConfirm
                                            handlers — refreshAllCharacteristics and
                                            refreshCharacteristicsAspect — ALSO address
                                            `categoryName`, so display and write already agree.
                                            Re-pointing only the DISPLAY would manufacture the very
                                            divergence being fixed; re-pointing BOTH changes WRITE
                                            TARGETING on two destructive controls, which
                                            task-87537g8s9a says needs its own review and decision
                                            rather than riding in on another change.
                                            Recorded here rather than in that row because a row
                                            body cannot be amended, and a finding that lives only in
                                            an inbox is one nobody re-reads. */}
                                        <PersonaRefreshConfirmDialog
                                            open={confirmRefreshAll}
                                            onOpenChange={setConfirmRefreshAll}
                                            sectorId={writeSectorId}
                                            categoryPath={categoryName}
                                            personas={storedPersonas}
                                            weightsJustWritten={weightsJustWritten}
                                            scope="all"
                                            loading={loadingCharacteristics}
                                            onConfirm={async () => {
                                                setConfirmRefreshAll(false);
                                                await refreshAllCharacteristics();
                                            }}
                                        />
                                        <PersonaRefreshConfirmDialog
                                            open={confirmPersonaRefresh}
                                            onOpenChange={setConfirmPersonaRefresh}
                                            sectorId={writeSectorId}
                                            categoryPath={categoryName}
                                            personas={personas}
                                            weightsJustWritten={weightsJustWritten}
                                            loading={refreshingAspect === 'personas'}
                                            onConfirm={async () => {
                                                setConfirmPersonaRefresh(false);
                                                await refreshCharacteristicsAspect('personas');
                                            }}
                                        />
                                        {hasPersonas ? (
                                            <PersonaGrid personas={personas} />
                                        ) : (
                                            /* This page DOES carry the labelled control, so the copy names it. */
                                            <PersonaEmptyState hasAction />
                                        )}
                                    </div>
                                        );
                                    })()}

                                    {/* Spec Semantics Section */}
                                    <div className="border rounded-lg p-4">
                                        <div className="flex items-center justify-between mb-4">
                                            <div className="flex items-center gap-2">
                                                <Ruler className="w-5 h-5 text-orange-600" />
                                                <h3 className="font-semibold">Spec Semantics</h3>
                                                {characteristics.aspects.spec_semantics?.hasData ? (
                                                    <Badge variant="default" className="text-xs">
                                                        {characteristics.aspects.spec_semantics.specCount} specs
                                                    </Badge>
                                                ) : (
                                                    <Badge variant="outline" className="text-xs">Not researched</Badge>
                                                )}
                                            </div>
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => setPendingRefresh('spec_semantics')}
                                                /* ⛔ DO NOT OPEN A "Replace" DIALOG FOR AN OPERATION GUARANTEED TO
                                                   REFUSE. The handler already fails closed on an unknown write
                                                   sector, so this is not data loss — but walking an operator
                                                   through a destructive confirmation that cannot succeed teaches
                                                   them the dialog is theatre. The admin page disables here; this
                                                   is its twin. */
                                                disabled={refreshingAspect === 'spec_semantics' || writeSectorId == null}
                                            >
                                                {refreshingAspect === 'spec_semantics' ? (
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                ) : (
                                                    <RefreshCw className="w-4 h-4" />
                                                )}
                                            </Button>
                                        </div>
                                        {characteristics.aspects.spec_semantics?.hasData ? (
                                            <div className="space-y-3">
                                                {Object.entries(characteristics.aspects.spec_semantics.data).slice(0, 5).map(([spec, data]: [string, any]) => (
                                                    <div key={spec} className="bg-muted/50 rounded-lg p-3">
                                                        <div className="font-medium text-sm">{data.display_name || spec}</div>
                                                        <div className="text-xs text-muted-foreground mt-1">{data.description}</div>
                                                        {data.ranges && (
                                                            <div className="mt-2 flex flex-wrap gap-1">
                                                                {data.ranges.map((range: any, i: number) => (
                                                                    <Badge key={i} variant="outline" className="text-xs">
                                                                        {range.range}: {range.label}
                                                                    </Badge>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-sm text-muted-foreground">
                                                No spec semantics researched yet. This helps explain what spec values mean (e.g., TPI 120 = premium).
                                            </p>
                                        )}
                                    </div>

                                    {/* Tier Vocabulary Section */}
                                    <div className="border rounded-lg p-4">
                                        <div className="flex items-center justify-between mb-4">
                                            <div className="flex items-center gap-2">
                                                <MessageSquare className="w-5 h-5 text-teal-600" />
                                                <h3 className="font-semibold">Tier Vocabulary</h3>
                                                {characteristics.aspects.tier_vocabulary?.hasData ? (
                                                    <Badge variant="default" className="text-xs">Researched</Badge>
                                                ) : (
                                                    <Badge variant="outline" className="text-xs">Not researched</Badge>
                                                )}
                                            </div>
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => setPendingRefresh('tier_vocabulary')}
                                                /* ⛔ DO NOT OPEN A "Replace" DIALOG FOR AN OPERATION GUARANTEED TO
                                                   REFUSE. The handler already fails closed on an unknown write
                                                   sector, so this is not data loss — but walking an operator
                                                   through a destructive confirmation that cannot succeed teaches
                                                   them the dialog is theatre. The admin page disables here; this
                                                   is its twin. */
                                                disabled={refreshingAspect === 'tier_vocabulary' || writeSectorId == null}
                                            >
                                                {refreshingAspect === 'tier_vocabulary' ? (
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                ) : (
                                                    <RefreshCw className="w-4 h-4" />
                                                )}
                                            </Button>
                                        </div>
                                        {characteristics.aspects.tier_vocabulary?.hasData ? (
                                            <div className="grid grid-cols-2 gap-4">
                                                {['entry', 'mid_range', 'premium', 'ultra_premium'].map((tier) => {
                                                    const vocab = characteristics.aspects.tier_vocabulary.data[tier];
                                                    if (!vocab) return null;
                                                    return (
                                                        <div key={tier} className="bg-muted/50 rounded-lg p-3">
                                                            <div className="font-medium text-sm capitalize mb-2">
                                                                {tier.replace('_', ' ')}
                                                            </div>
                                                            <div className="space-y-2">
                                                                <div>
                                                                    <span className="text-xs text-green-600 font-medium">Use:</span>
                                                                    <div className="flex flex-wrap gap-1 mt-1">
                                                                        {vocab.use?.slice(0, 4).map((term: string, i: number) => (
                                                                            <Badge key={i} variant="secondary" className="text-xs bg-green-50">
                                                                                {term}
                                                                            </Badge>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                                <div>
                                                                    <span className="text-xs text-red-600 font-medium">Avoid:</span>
                                                                    <div className="flex flex-wrap gap-1 mt-1">
                                                                        {vocab.avoid?.slice(0, 4).map((term: string, i: number) => (
                                                                            <Badge key={i} variant="secondary" className="text-xs bg-red-50">
                                                                                {term}
                                                                            </Badge>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <p className="text-sm text-muted-foreground">
                                                No tier vocabulary researched yet. This guides what language to use/avoid per price tier.
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Research Sessions Tab */}
                <TabsContent value="sessions">
                    <Card>
                        <CardHeader>
                            <CardTitle>Research Sessions</CardTitle>
                            <CardDescription>History of category perception research</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Category</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead>Attributes Found</TableHead>
                                        <TableHead>Created</TableHead>
                                        <TableHead>Completed</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {sessions.map((session) => (
                                        <TableRow key={session.id}>
                                            <TableCell className="font-medium">{session.category_name}</TableCell>
                                            <TableCell>{getStatusBadge(session.status)}</TableCell>
                                            <TableCell>{session.parsed_attributes_count}</TableCell>
                                            <TableCell>{new Date(session.created_at).toLocaleString()}</TableCell>
                                            <TableCell>
                                                {session.completed_at
                                                    ? new Date(session.completed_at).toLocaleString()
                                                    : '-'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            {/* Edit Dialog */}
            <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>Edit Perceived Attribute</DialogTitle>
                        <DialogDescription>Update the attribute details</DialogDescription>
                    </DialogHeader>
                    {selectedPerception && (
                        <div className="space-y-4">
                            <div>
                                <Label>Attribute Name</Label>
                                <Input
                                    value={selectedPerception.attribute_name}
                                    onChange={(e) =>
                                        setSelectedPerception({
                                            ...selectedPerception,
                                            attribute_name: e.target.value
                                        })
                                    }
                                />
                            </div>
                            <div>
                                <Label>Customer Description</Label>
                                <Textarea
                                    value={selectedPerception.customer_description}
                                    onChange={(e) =>
                                        setSelectedPerception({
                                            ...selectedPerception,
                                            customer_description: e.target.value
                                        })
                                    }
                                    rows={3}
                                />
                            </div>
                            <div>
                                <Label>Common Phrases (comma-separated)</Label>
                                <Textarea
                                    value={selectedPerception.common_phrases?.join(', ')}
                                    onChange={(e) =>
                                        setSelectedPerception({
                                            ...selectedPerception,
                                            common_phrases: e.target.value.split(',').map(s => s.trim())
                                        })
                                    }
                                    rows={3}
                                />
                            </div>
                            <div>
                                <Label>Technical Features (comma-separated)</Label>
                                <Textarea
                                    value={selectedPerception.technical_features?.join(', ')}
                                    onChange={(e) =>
                                        setSelectedPerception({
                                            ...selectedPerception,
                                            technical_features: e.target.value.split(',').map(s => s.trim())
                                        })
                                    }
                                    rows={2}
                                />
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleSaveEdit}>Save Changes</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* ⛔ ONE DIALOG FOR EVERY DESTRUCTIVE REBUILD ON THIS PAGE. Mounted once rather than
                beside each button so the six controls cannot drift apart in what they warn about —
                the previous guard covered one button and left the blue one running straight
                through, which is worse than none because the docs then promise a confirmation
                that never appears. */}
            <RefreshConfirmDialog
                open={pendingRefresh !== null}
                onOpenChange={(o) => { if (!o) setPendingRefresh(null); }}
                scope={pendingRefresh ?? 'all'}
                categoryPath={categoryName}
                /* ⛔ NAME THE SECTOR THE WRITE WILL USE — and it is now the SAME VALUE the handlers
                   take, so the dialog cannot name one row while the write lands on another. When
                   that value is unknown the handlers refuse and this renders "refusing to guess". */
                sectorId={writeSectorId}
                /* ⛔ NULL ONLY WHEN GENUINELY UNKNOWN — and destroy-0 has a THIRD direction.
                   `characteristics === null` is not one state: once characteristicsKnown is true the
                   fetch HAS completed and the row is genuinely absent, so passing null there made
                   the dialog say it cannot tell how many personas exist on a FIRST research — the
                   one case that destroys nothing at all. Overstating the risk on the safe action
                   trains click-through just as fast as understating it on the dangerous one.
                   ⇒ Known-absent is 0. Null is reserved for unresolved or failed. */
                personaCount={
                    characteristics
                        ? (characteristics.aspects.personas?.data?.length ?? 0)
                        : characteristicsKnown ? 0 : null
                }
                loading={loadingCharacteristics || refreshingAspect !== null}
                onConfirm={() => {
                    const scope = pendingRefresh;
                    setPendingRefresh(null);
                    if (scope === 'all') refreshAllCharacteristics();
                    else if (scope) refreshCharacteristicsAspect(scope);
                }}
            />
        </div>
    );
}
